"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.MediaController = void 0;
const domain_id_1 = require("@streem/domain-id");
const logger_1 = require("@streem/logger");
const toolbox_1 = require("@streem/toolbox");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const streem_sdk_protobuf_1 = require("streem-sdk-protobuf");
const logger_2 = require("../../../util/logger");
const track_constraints_1 = require("../../../util/track_constraints");
const utils_1 = require("../../wall/utils");
const audio_controllers_1 = require("../audio_controllers");
const data_controller_1 = require("../data_controller");
const media_builder_1 = require("./media_builder");
const video_state_1 = require("./video_state");
const filter_null_1 = require("../../../util/rx/filter_null");
class MediaController {
    constructor(config, observables, trackProvider) {
        this.config = config;
        this.destroyed = new rxjs_1.Subject();
        (0, toolbox_1.invariant)(this.config.context.localUserId, 'Expected localUserId to be defined when initializing MediaController');
        (0, toolbox_1.invariant)(this.config.context.remoteUserId, 'Expected remoteUserId to be defined when initializing MediaController');
        (0, toolbox_1.invariant)(this.config.context.callSettings, 'Expected callSettings to be defined when initializing MediaController');
        this.trackProvider = trackProvider;
        const trackProviderName = this.trackProvider.trackProviderName;
        logger_2.logger.debug(`Your track provider is ${trackProviderName}`);
        logger_2.logger.setContextValue(logger_1.contextValues.TRACK_PROVIDER, trackProviderName);
        this.mediaBuilder = new media_builder_1.MediaBuilder(this.trackProvider, this.config.roomWall);
        this.videoState = new video_state_1.VideoState(Object.assign(Object.assign({}, config), { trackProvider: this.trackProvider }));
        this.videoControllers = this.videoState.values;
        this.localVideoController = this.videoState.localController;
        this.localAudio = new audio_controllers_1.LocalAudioController({
            trackSource: this.trackProvider.localAudioTrack,
            userId: this.config.context.localUserId,
            roomId: this.config.context.roomId,
            roomWall: this.config.roomWall,
            wallItem: this.config.roomWall.itemsByType.pipe((0, utils_1.findInGroup)('audioTrack'), (0, utils_1.accumulateAndMapWallEvents)('audioTrack'), (0, operators_1.map)(audioTrackWallItems => {
                const [latestTrack] = audioTrackWallItems
                    .filter(item => { var _a; return item.ownerUserId === ((_a = this.config.context.localUserId) === null || _a === void 0 ? void 0 : _a.toString()); })
                    .reverse();
                return latestTrack;
            })),
        });
        this.remoteAudio = new audio_controllers_1.RemoteAudioController({
            trackSource: this.trackProvider.remoteAudioTrack,
            userId: this.config.context.remoteUserId,
            roomId: this.config.context.roomId,
            roomWall: this.config.roomWall,
            wallItem: this.config.roomWall.itemsByType.pipe((0, utils_1.findInGroup)('audioTrack'), (0, utils_1.accumulateAndMapWallEvents)('audioTrack'), (0, operators_1.map)(audioTrackWallItems => {
                const [latestTrack] = audioTrackWallItems
                    .filter(item => { var _a; return item.ownerUserId === ((_a = this.config.context.remoteUserId) === null || _a === void 0 ? void 0 : _a.toString()); })
                    .reverse();
                return latestTrack;
            })),
        });
        this.data = new data_controller_1.DataController(this.trackProvider.localDataTrack, this.trackProvider.remoteDataTrack, this.config.context.localUserId);
        this.remoteNetworkQuality = this.trackProvider.remoteNetworkQuality;
        this.localNetworkQuality = this.trackProvider.localNetworkQuality;
        this.getUserMediaVideoError = this.trackProvider.getUserMediaVideoError.pipe((0, filter_null_1.filterNull)());
        this.getUserMediaAudioError = this.trackProvider.getUserMediaAudioError.pipe((0, filter_null_1.filterNull)());
        this.roomWebRtcStats = this.trackProvider.mediaWebRtcStats.pipe((0, filter_null_1.filterNull)(), (0, operators_1.takeUntil)(this.destroyed));
        if (this.trackProvider.isLocalVideoFrozen !== undefined) {
            this.isLocalVideoFrozen = this.trackProvider.isLocalVideoFrozen.pipe((0, filter_null_1.filterNull)(), (0, operators_1.takeUntil)(this.destroyed));
        }
        const videoTracks = this.config.roomWall.itemsByType.pipe((0, utils_1.findInGroup)('videoTrack'), (0, utils_1.accumulateAndMapWallEvents)('videoTrack'), (0, operators_1.takeUntil)(this.destroyed));
        /**
         *
         * Create a VideoController for a VideoTrack WallItem
         * if it has not been created yet.
         *
         */
        videoTracks
            .pipe((0, operators_1.takeUntil)(this.destroyed))
            .subscribe((wallItems) => {
            var _a;
            let j = wallItems.length;
            while (j--) {
                this.videoState.createNewController({
                    wallItemId: wallItems[j].id,
                    ownerUserId: new domain_id_1.UserId(wallItems[j].ownerUserId),
                    videoTrackName: (_a = wallItems[j].name) !== null && _a !== void 0 ? _a : undefined,
                });
            }
        });
        this.remoteParticipantCapabilities = observables.remoteParticipantCapabilities;
        this.localVideoLabel = this.trackProvider.localVideoTrack.pipe((0, filter_null_1.filterNull)(), (0, operators_1.map)(track => track.trackLabel), (0, operators_1.takeUntil)(this.destroyed));
        this.localAudioLabel = this.trackProvider.localAudioTrack.pipe((0, filter_null_1.filterNull)(), (0, operators_1.map)(track => track.trackLabel), (0, operators_1.takeUntil)(this.destroyed));
        this.localVideoLabel.subscribe(label => {
            logger_2.logger.info(`Call initiated with ${label} camera`);
        });
        this.localAudioLabel.subscribe(label => {
            logger_2.logger.info(`Call initiated with ${label} microphone`);
        });
        if (this.trackProvider.localDevices !== undefined) {
            this.localDevices = this.trackProvider.localDevices.pipe((0, filter_null_1.filterNull)(), (0, operators_1.takeUntil)(this.destroyed));
        }
        else {
            this.localDevices = (0, rxjs_1.of)([]);
        }
        this.hasRemoteUserConnection = this.trackProvider.remoteConnection;
        this.connectionState = this.trackProvider.connectionState;
    }
    selectMic({ deviceId, label, groupId, }) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.localAudio.restart({ deviceId, groupId });
            logger_2.logger.info(`Expert changed microphone to: ${label}`);
        });
    }
    selectSpeaker({ deviceId, label }) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.remoteAudio.selectSpeaker(deviceId);
            logger_2.logger.info(`Expert changed speaker to: ${label}`);
        });
    }
    getStats() {
        return __awaiter(this, void 0, void 0, function* () {
            (0, toolbox_1.invariant)(this.trackProvider.getStats, 'Attempted to get stats on a remote media track. Did you mean to call this on the local track instead?');
            return yield this.trackProvider.getStats();
        });
    }
    connect(roomId, userId) {
        return __awaiter(this, void 0, void 0, function* () {
            (0, rxjs_1.combineLatest)([this.localAudio.connected, this.localAudio.enabled])
                .pipe((0, operators_1.takeUntil)(this.destroyed))
                .subscribe(([connected, enabled]) => {
                if (connected) {
                    const item = this.config.roomWall.builder.buildTrackWallItem('audioTrack', {
                        active: enabled,
                    });
                    this.config.roomWall.addOrUpdate(item);
                }
                else {
                    this.config.roomWall.delete('audioTrack');
                }
            });
            this.trackProvider.localDataTrack
                .pipe((0, operators_1.takeUntil)(this.destroyed))
                .subscribe(track => track
                ? this.config.roomWall.addOrUpdate(this.config.roomWall.builder.buildTrackWallItem('dataTrack', {}))
                : this.config.roomWall.delete('dataTrack'));
            yield this.trackProvider.connect(roomId, userId);
        });
    }
    disconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.destroyed.next(undefined);
            this.videoState.clearState();
            this.data.disconnect();
            this.localAudio.disconnect();
            this.remoteAudio.disconnect();
            yield this.trackProvider.disconnect();
        });
    }
    createSpectatorTracks() {
        return __awaiter(this, void 0, void 0, function* () {
            (0, toolbox_1.invariant)(this.config.context.callSettings); // this should be caught in construction
            const faceToFaceModeEnabled = this.config.context.callSettings.faceToFaceModeEnabled;
            const cameraSubject = streem_sdk_protobuf_1.CameraSubject.CAMERA_SUBJECT_PERSON;
            const preferredCameraId = (0, toolbox_1.getPreferredVideoInputId)();
            const preferredMicId = (0, toolbox_1.getPreferredAudioInputId)();
            const preferredAudioOutputDeviceId = (0, toolbox_1.getPreferredAudioOutputId)();
            yield this.mediaBuilder.createMedia({
                preferredAudioOutputDeviceId: preferredAudioOutputDeviceId !== null && preferredAudioOutputDeviceId !== void 0 ? preferredAudioOutputDeviceId : 'default',
                constraints: Object.assign({ audio: preferredMicId ? { deviceId: preferredMicId } : true }, (faceToFaceModeEnabled && {
                    video: yield (0, track_constraints_1.generateStreemVideoTrackConstraints)(cameraSubject, preferredCameraId),
                })),
                video: {
                    name: (() => {
                        if (faceToFaceModeEnabled && this.trackProvider.useNamedVideoTracks) {
                            return (0, track_constraints_1.createVideoTrackName)(this.config.context.localDeviceId, cameraSubject);
                        }
                        else {
                            return '';
                        }
                    })(),
                    cameraSubject: cameraSubject,
                    active: true,
                },
            });
        });
    }
    createHostTracks() {
        return __awaiter(this, void 0, void 0, function* () {
            (0, toolbox_1.invariant)(this.config.context.callSettings); // this should be caught in construction
            const faceToFaceModeEnabled = this.config.context.callSettings.faceToFaceModeEnabled;
            const cameraSubject = faceToFaceModeEnabled
                ? streem_sdk_protobuf_1.CameraSubject.CAMERA_SUBJECT_PERSON
                : streem_sdk_protobuf_1.CameraSubject.CAMERA_SUBJECT_WORLD;
            yield this.mediaBuilder.createMedia({
                constraints: {
                    audio: true,
                    video: yield (0, track_constraints_1.generateStreemVideoTrackConstraints)(cameraSubject),
                },
                video: {
                    name: (() => {
                        if (faceToFaceModeEnabled && this.trackProvider.useNamedVideoTracks) {
                            return (0, track_constraints_1.createVideoTrackName)(this.config.context.localDeviceId, cameraSubject);
                        }
                        else {
                            return '';
                        }
                    })(),
                    cameraSubject: cameraSubject,
                    active: true,
                },
            });
        });
    }
}
exports.MediaController = MediaController;
//# sourceMappingURL=media_controller.js.map